import React, { useEffect, useMemo, useState } from "react";
import { Title } from "rbx";
import { useMutation, useQuery, useLazyQuery } from "@apollo/client";
import Select from "react-select";
import { format } from "date-fns";

import { Loader } from "../../components";
import { GET_ALL_REVIEW_CYCLES } from "../ReviewCycle/graphql";
import { LIST_EMPLOYEES_FOR_REVIEW_CYCLES } from "./graphql";
import { GET_REVIEWS_WITH_ONLY_EVALUATIONS } from "../Reviews/graphql";
import { useModal } from "../../context";
import { getObjectIdTimestamp } from "../../utils";

import "./ReviewCycleUsers.scss";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const GROUPS = ["D1", "D2", "D3", "D4", "D5"];

const ReviewCycleUsers = () => {
  const { setModalOpen } = useModal();

  const { data: cyclesData, loading: cyclesLoading } = useQuery(
    GET_ALL_REVIEW_CYCLES,
    {
      variables: {},
      fetchPolicy: "network-only",
    }
  );

  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState("ALL");
  const [users, setUsers] = useState([]);

  const [loadUsers, { loading: usersLoading }] = useMutation(
    LIST_EMPLOYEES_FOR_REVIEW_CYCLES
  );
  const [loadReviews] = useLazyQuery(GET_REVIEWS_WITH_ONLY_EVALUATIONS);

  const monthOptions = useMemo(
    () => [
      { value: "ALL", label: "All" },
      ...MONTHS.map((m) => ({ value: m.toLowerCase(), label: m })),
    ],
    []
  );

  const groupOptions = useMemo(
    () => [
      { value: "ALL", label: "All" },
      ...GROUPS.map((g) => ({ value: g, label: g })),
    ],
    []
  );

  const getMatchingReviewCycleIds = (groupValue, monthValue) => {
    const cycles = cyclesData?.reviewCycles || [];
    const monthStr = monthValue && monthValue !== "ALL" ? monthValue : null;
    const groupStr = groupValue && groupValue !== "ALL" ? groupValue : null;

    return cycles
      .filter((cycle) => {
        const text = `${cycle.groupCycle || ""} ${cycle.name || ""}`.toLowerCase();
        const matchesGroup = groupStr
          ? text.startsWith(groupStr.toLowerCase())
          : true;
        const matchesMonth = monthStr ? text.includes(monthStr) : true;
        return matchesGroup && matchesMonth;
      })
      .map((c) => c.id);
  };

  const fetchUsers = async (groupValue, monthValue) => {
    const ids = getMatchingReviewCycleIds(groupValue, monthValue);

    if (!ids || !ids.length) {
      setUsers([]);
      return;
    }

    const { data } = await loadUsers({
      variables: { reviewCycleIds: ids },
    });

    const items =
      data?.listofemployeesforreviecyclegivenreviewcycleid || [];

    // Flatten and de-duplicate users across all matching review cycles
    const map = new Map();
    items.forEach((group) => {
      (group?.users || []).forEach((u) => {
        if (!u?.id) return;
        if (!map.has(u.id)) {
          map.set(u.id, u);
        }
      });
    });

    setUsers(Array.from(map.values()));
  };

  // Initialize defaults: month = ALL, group = ALL
  useEffect(() => {
    if (!cyclesData || selectedMonth !== null) return;

    setSelectedMonth(null);
    setSelectedGroup("ALL");
    fetchUsers("ALL", "ALL");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cyclesData]);

  const handleMonthChange = (option) => {
    const value = option?.value || "ALL";
    setSelectedMonth(value === "ALL" ? null : value);
    fetchUsers(selectedGroup, value);
  };

  const handleGroupChange = (option) => {
    const value = option?.value || "ALL";
    setSelectedGroup(value);
    fetchUsers(value, selectedMonth || "ALL");
  };

  if (cyclesLoading || (usersLoading && !users.length)) {
    return (
      <div className="review-cycle-users-container">
        <Loader />
      </div>
    );
  }

  const selectedMonthOption =
    monthOptions.find((o) => o.value === (selectedMonth || "ALL")) ||
    monthOptions[0];

  const selectedGroupOption =
    groupOptions.find((o) => o.value === selectedGroup) || groupOptions[0];

  const handleRowClick = async (user) => {
    try {
      // Show loading state inside modal immediately
      setModalOpen(
        true,
        <div className="review-cycle-users-modal">
          <div className="review-cycle-users-modal-header">
            <h3>
              Loading reviews for {user.firstName} {user.lastName} (
              {user.employeeNumber})...
            </h3>
            <button
              type="button"
              className="review-cycle-users-modal-close"
              onClick={() => setModalOpen(false, "")}
            >
              ×
            </button>
          </div>
          <Loader />
        </div>
      );

      const { data } = await loadReviews({
        variables: {
          where: {
            evaluations: {
              some: {
                ownerEmployeeNumber: {
                  contains: user.employeeNumber,
                },
              },
            },
          },
        },
        fetchPolicy: "network-only",
      });

      const reviews = data?.reviews || [];

      const ReviewListModal = () => (
        <div className="review-cycle-users-modal">
          <div className="review-cycle-users-modal-header">
            <h3>
              Reviews for {user.firstName} {user.lastName} (
              {user.employeeNumber})
            </h3>
            <button
              type="button"
              className="review-cycle-users-modal-close"
              onClick={() => setModalOpen(false, "")}
            >
              ×
            </button>
          </div>
          {reviews.length === 0 ? (
            <p>No reviews found for this user.</p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Status</th>
                  <th>Year</th>
                  <th>Due Date</th>
                  <th>Date Completed</th>
                </tr>
              </thead>
              <tbody>
                {reviews.map((r) => {
                  const createdAt = getObjectIdTimestamp(r.id);
                  const year =
                    createdAt?.getFullYear().toString() || r.year || "N/A";
                  return (
                    <tr key={r.id}>
                      <td>{r.title}</td>
                      <td>{r.status}</td>
                      <td>{year}</td>
                      <td>
                        {r.dateToCompleteBy
                          ? format(new Date(r.dateToCompleteBy), "MM/dd/yyyy")
                          : ""}
                      </td>
                      <td>
                        {r.dateCompleted
                          ? format(new Date(r.dateCompleted), "MM/dd/yyyy")
                          : ""}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      );

      setModalOpen(true, <ReviewListModal />);
    } catch (e) {
      const ErrorModal = () => (
        <div className="review-cycle-users-modal">
          <div className="review-cycle-users-modal-header">
            <h3>Error loading reviews</h3>
            <button
              type="button"
              className="review-cycle-users-modal-close"
              onClick={() => setModalOpen(false, "")}
            >
              ×
            </button>
          </div>
          <p>Failed to load reviews for this user. Please try again.</p>
        </div>
      );

      setModalOpen(true, <ErrorModal />);
    }
  };

  return (
    <div className="review-cycle-users-container">
      <div className="review-cycle-users-header">
        <Title className="title">Review Cycles Users</Title>
      </div>

      <div className="review-cycle-users-filters">
        <div className="filter">
          <span className="filter-label">Month</span>
          <Select
            classNamePrefix="select"
            options={monthOptions}
            value={selectedMonthOption}
            onChange={handleMonthChange}
          />
        </div>
        <div className="filter">
          <span className="filter-label">Group</span>
          <Select
            classNamePrefix="select"
            options={groupOptions}
            value={selectedGroupOption}
            onChange={handleGroupChange}
          />
        </div>
      </div>

      <div className="review-cycle-users-table-wrapper">
        <table className="review-cycle-users-table">
          <thead>
            <tr>
              <th>Employee #</th>
              <th>First Name</th>
              <th>Last Name</th>
              <th>Group</th>
              <th>Date Hired</th>
              <th>Date Rehired</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr
                key={u.id || u.employeeNumber}
                className="review-cycle-users-row"
                onClick={() => handleRowClick(u)}
              >
                <td>{u.employeeNumber}</td>
                <td>{u.firstName}</td>
                <td>{u.lastName}</td>
                <td>{u.group}</td>
                <td>
                  {u.dateHired
                    ? format(new Date(u.dateHired), "MM/dd/yyyy")
                    : ""}
                </td>
                <td>
                  {u.dateRehired
                    ? format(new Date(u.dateRehired), "MM/dd/yyyy")
                    : ""}
                </td>
              </tr>
            ))}
            {!users.length && (
              <tr>
                <td colSpan={6} style={{ textAlign: "center" }}>
                  No users found for the selected filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ReviewCycleUsers;



